import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

interface Module {
  module_name: string;
  is_enabled: boolean;
  config: any;
  updated_at: string;
}

export const ModuleManagement: React.FC = () => {
  const { language } = useLanguage();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchModules = async () => {
    try {
      const res = await api.get('/modules');
      setModules(res.data);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load modules');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const toggleModule = async (moduleName: string, currentState: boolean) => {
    try {
      await api.put('/modules/toggle', { moduleName, isEnabled: !currentState });
      await fetchModules();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to toggle module');
    }
  };

  const getModuleLabel = (name: string) => {
    const mapEn: Record<string, string> = {
      brokerage: 'Brokerage Integration (Alpaca/IBKR)',
      tax_harvest: 'Tax-Loss Harvesting',
      rebalancing: 'Portfolio Rebalancing',
      ml_predict: 'ML Price Predictions',
      email_sync: 'Email Trade Import (IMAP)',
      alerts: 'Price Alerts & Notifications',
      websocket: 'Live WebSocket Price Updates',
      analytics: 'Advanced Analytics (Sharpe, VaR)',
      efficient_frontier: 'Efficient Frontier Plotter',
      macro_stress: 'Macro Stress-Test Simulator',
      sentiment: 'Sentiment Index & News Signal',
      correlation_heatmap: 'Interactive Correlation Heatmap',
      backtesting: 'Advanced Backtesting Simulator',
      currency_fx: 'FX Multi-Currency Impact',
      dividends: 'Dividend Income Stream Tracker',
      risk_alerts: 'Proactive Portfolio Risk Alerts',
      transaction_ledger: 'Manual Transaction Ledger',
      imap_parse_log: 'IMAP Statement Sync Log',
      pdf_reporting: 'PDF Performance Report Builder',
      macro_sensitivity: 'Macro Factor Sensitivity',
      whatif_sandbox: 'What-If Simulation Sandbox',
    };
    const mapSk: Record<string, string> = {
      brokerage: 'Integrácia brokera (Alpaca/IBKR)',
      tax_harvest: 'Sledovanie daňových strát',
      rebalancing: 'Rebalansovanie portfólia',
      ml_predict: 'ML predikcie cien',
      email_sync: 'Import obchodov z emailov (IMAP)',
      alerts: 'Cenové upozornenia a notifikácie',
      websocket: 'Živé WebSocket aktualizácie cien',
      analytics: 'Pokročilá analytika (Sharpe, VaR)',
      efficient_frontier: 'Vykreslenie efektívnej hranice',
      macro_stress: 'Simulátor makro záťažových testov',
      sentiment: 'Index sentimentu a správ',
      correlation_heatmap: 'Interaktívna matica korelácií',
      backtesting: 'Simulátor spätného testovania',
      currency_fx: 'Sledovanie menových vplyvov (FX)',
      dividends: 'Prehľad a plán dividendových výnosov',
      risk_alerts: 'Proaktívne rizikové upozornenia',
      transaction_ledger: 'Manuálna kniha transakcií',
      imap_parse_log: 'Log importov z emailov',
      pdf_reporting: 'Generátor PDF reportov výkonnosti',
      macro_sensitivity: 'Analýza citlivosti na makro faktory',
      whatif_sandbox: 'What-If Simulátor portfólia',
    };
    return language === 'sk' ? (mapSk[name] || name) : (mapEn[name] || name);
  };

  const getModuleIcon = (name: string, enabled: boolean) => {
    const icons: Record<string, string> = {
      brokerage: '🔌',
      tax_harvest: '💰',
      rebalancing: '⚖️',
      ml_predict: '🧠',
      email_sync: '📧',
      alerts: '🔔',
      websocket: '📡',
      analytics: '📊',
      efficient_frontier: '📈',
      macro_stress: '🌪️',
      sentiment: '📰',
      correlation_heatmap: '🎨',
      backtesting: '📉',
      currency_fx: '💱',
      dividends: '📊',
      risk_alerts: '🚨',
      transaction_ledger: '📝',
      imap_parse_log: '📂',
      pdf_reporting: '📄',
      macro_sensitivity: '🧠',
      whatif_sandbox: '🔬',
    };
    return `${icons[name] || '📦'} ${enabled ? '🟢' : '🔴'}`;
  };

  if (loading) return <div className="glass-panel p-8 text-center text-stone-400 font-mono text-xs animate-pulse">{language === 'sk' ? 'Načítavam zoznam modulov...' : 'Loading module directory data...'}</div>;
  if (error) return <div className="glass-panel p-8 text-center text-rose-600 font-mono text-xs">{error}</div>;

  return (
    <div className="glass-panel p-6 space-y-6 font-mono text-xs">
      <div className="flex justify-between items-center pb-4 border-b border-[var(--border-color)]">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
            <span>🛡️</span>
            {language === 'sk' ? 'Správa Modulov Systému' : 'System Module Control Panel'}
          </h3>
          <p className="text-[10px] text-stone-500 mt-1">
            {language === 'sk' 
              ? 'Zapínanie a vypínanie funkčných modulov portfóliového engine' 
              : 'Dynamic feature flags to enable/disable portfolio engine modules for your tenant'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((mod) => (
          <div
            key={mod.module_name}
            className={`bg-white border-2 border-stone-900 rounded-2xl p-4 transition-all shadow-[4px_4px_0px_0px_rgba(28,25,23,1)] ${
              mod.is_enabled ? 'border-stone-900' : 'opacity-70 border-dashed'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">{getModuleIcon(mod.module_name, mod.is_enabled)}</span>
                <div>
                  <div className="font-extrabold text-[#1c1917] text-xs">{getModuleLabel(mod.module_name)}</div>
                  <div className="text-[9px] text-stone-400 font-mono mt-0.5">{mod.module_name}</div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={mod.is_enabled}
                  onChange={() => toggleModule(mod.module_name, mod.is_enabled)}
                  className="sr-only peer"
                />
                <div className="w-10 h-6 bg-stone-200 border-2 border-stone-900 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-stone-900 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-400"></div>
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

